import { filterProfanity } from '../../../tool/profanityFilter'
import {
  SESSION_TAXONOMY,
  TAXONOMY,
  WINDOW_TAXONOMY,
  getStudentCategories,
  getStudentQueryWindows,
} from '../../instructorDashboard/components/studentTaxonomy'
import { autoSegmentEvents } from '../../workspaceReplay2/utils/autoSegmentation'

// The experiment page's Overview: every condition's students as one class, with nothing that
// tells which condition a student is in, nor who they are - a student is 'Student N'
// (studentNumbers). The figures come from the Monitor's own code - computeTelemetryStats
// (stats) and the AI-use taxonomy (studentTaxonomy, on the parts of the Monitor's per-student
// analytics it reads) - run on the conditions' telemetry merged by mergeConditions, and the
// common errors (commonErrors) and the tests students are stuck on (stuckTests) from the same
// telemetry. All of it is rule-based: nothing here asks an AI model. What students wrote is
// shown through the project's word filter (filteredText).

// A student's Monitor row name (ClassOverview's rule).
export const displayName = (student) =>
  student.display_name || student.username || `${student.first_name || ''} ${student.last_name || ''}`.trim() || 'Unknown'

// Students only: staff who open a condition (an instructor trying the exercise) are not class.
export const isStudentRow = (user) => user?.role === 3 || user?.role === 4

// Whether a condition's Monitor lists a student with these events there (InstructorDashboard's
// rule, also computeAnalyticsById's and computeTelemetryStats'): at least 15 s from their first
// event to their last, and something besides SESSION_START.
export const listedByMonitor = (events) => {
  const first = events?.[0]?.timestamp
  const last = events?.[events.length - 1]?.timestamp
  return Boolean(first && last && last - first >= 15000) && events.some((event) => event.type !== 'SESSION_START')
}

// A student working this long with no test passing is flagged as stuck.
export const STUCK_AFTER_SECONDS = 10 * 60
// A student who has not finished and shows no activity for this long is flagged inactive.
export const INACTIVE_AFTER_MS = 5 * 60 * 1000

/**
 * Every condition's telemetry and roster, as one class.
 *   conditions: [{ key, sessionId, aiChat }] - aiChat as published (the experiment's config),
 *     so a student still counts as having had AI chat after End Session turns it off.
 *   eventsBySession / rosterBySession / fetchedAtBySession: per session id, as
 *     useConditionTelemetry holds them; a session not loaded yet is missing. A session's
 *     telemetry waits for its roster, the only thing that tells its students from staff.
 * Returns
 *   eventsByUser: one entry per student. A student on two conditions (a 'By link' experiment
 *     where they opened two links) is counted once, with both sessions' events in time order.
 *   sessionsByUser: user id -> { session id -> their events there }, the same events kept
 *     apart per condition session - each session is its own workspace, with its own code.
 *   students: the class, one row per student.
 *   homeByUser: user id -> the key of the condition to open the student in: of the conditions
 *     whose Monitor lists them (listedByMonitor on their events there), the one they were last
 *     active in. A student no condition's Monitor lists yet has none.
 *   aiUserIds: students who had AI chat - their home condition has it - or asked the AI anyway.
 *   fetchedAtByUser: user id -> when the telemetry their events come from was fetched (the
 *     earlier time, for a student with events in two conditions).
 */
export const mergeConditions = (conditions, eventsBySession, rosterBySession, fetchedAtBySession = {}) => {
  const eventsByUser = {}
  const sessionsByUser = {}
  const students = new Map()
  const home = new Map()
  const fetchedAt = new Map()
  const inTwo = new Set()

  for (const condition of conditions) {
    const roster = rosterBySession[condition.sessionId]
    if (!roster) continue
    const onRoster = new Set()
    for (const user of roster) {
      if (!isStudentRow(user)) continue
      const id = String(user.id)
      onRoster.add(id)
      if (!students.has(id)) students.set(id, user)
    }

    for (const [userId, events] of Object.entries(eventsBySession[condition.sessionId] || {})) {
      const id = String(userId)
      // Only the roster's students.
      if (!events?.length || !onRoster.has(id)) continue
      if (eventsByUser[id]) inTwo.add(id)
      eventsByUser[id] = eventsByUser[id] ? eventsByUser[id].concat(events) : events
      if (!sessionsByUser[id]) sessionsByUser[id] = {}
      sessionsByUser[id][condition.sessionId] = events
      fetchedAt.set(id, Math.min(fetchedAt.get(id) ?? Infinity, fetchedAtBySession[condition.sessionId] ?? Infinity))
      if (!listedByMonitor(events)) continue
      const last = events[events.length - 1].timestamp
      if (!home.has(id) || last > home.get(id).last) home.set(id, { condition, last })
    }
  }
  for (const id of inTwo) eventsByUser[id] = eventsByUser[id].slice().sort((a, b) => a.timestamp - b.timestamp)

  const aiUserIds = new Set(
    Object.keys(eventsByUser).filter(
      (id) => home.get(id)?.condition.aiChat || eventsByUser[id].some((event) => event.type === 'CHAT_QUERY')
    )
  )

  return {
    eventsByUser,
    sessionsByUser,
    students: [...students.values()],
    homeByUser: Object.fromEntries([...home].map(([id, { condition }]) => [id, condition.key])),
    aiUserIds,
    fetchedAtByUser: Object.fromEntries([...fetchedAt].filter(([, time]) => time !== Infinity)),
  }
}

// User ids in order: as numbers, for ids that are.
const compareIds = (a, b) => Number(a) - Number(b) || String(a).localeCompare(String(b))

/**
 * Each student's number on the Overview, user id -> N, shown as 'Student N' (studentLabel):
 * in the order of their first telemetry event of any kind (events[0] - mergeConditions and the
 * backend keep a student's events in time order), ties by user id. A student with no events
 * has none. `eventsByUser`: mergeConditions'.
 *   kept: the numbers given so far (this function's last answer), kept as they are; a student
 *     new since is numbered after them. A student who started earlier but shows up later - a
 *     telemetry batch still on its way at the last poll, a condition whose requests failed -
 *     gets the next number rather than moving everyone after them up by one.
 * Returns `kept` itself when nobody is new.
 */
export const studentNumbers = (eventsByUser, kept = {}) => {
  const fresh = Object.entries(eventsByUser)
    .filter(([id, events]) => events?.length && !Object.prototype.hasOwnProperty.call(kept, id))
    .map(([id, events]) => ({ id, first: events[0].timestamp }))
    .sort((a, b) => a.first - b.first || compareIds(a.id, b.id))
  if (!fresh.length) return kept
  let last = Math.max(0, ...Object.values(kept))
  return { ...kept, ...Object.fromEntries(fresh.map(({ id }) => [id, (last += 1)])) }
}

// A student's name on the Overview: 'Student N' (studentNumbers), never their own.
export const studentLabel = (numbers, id) => {
  const number = numbers?.[String(id)]
  return number ? `Student ${number}` : 'Student'
}

// How long the texts filteredText keeps may be in all, in characters: far more than one render
// of the Overview shows - ten thousand error keys at their longest (ERROR_KEY_MAX), as Common
// errors' Show all may list them, or a hundred programs of 20,000 characters. By length rather
// than by count, so that thousands of error keys fit while long code stays bounded: a render
// that shows more texts than are kept, in the same order each time, drops each one just before
// it is shown again, and so filters every one of them at every poll.
export const FILTERED_TEXT_CHARS = 2000000
// filteredText's results by text, the one shown least recently first, and how long those texts
// are in all.
const filteredTexts = new Map()
let filteredChars = 0

/**
 * What a student wrote, as the Overview shows it - an AI question, a run's code, an error's
 * message or key: through the project's word filter (filterProfanity), which masks each word
 * on its list with '*'s, as the older group chat and code views do. For display only, on what
 * is on screen: errors are grouped and counted by what was written, so two words masked alike
 * never make one error. bad-words checks every word of a text against its whole list, which
 * takes long on a run's code, and each poll and re-render shows the same texts again: each
 * text's result is kept, for the texts shown last, up to FILTERED_TEXT_CHARS characters in all.
 * Anything but a non-empty string is returned as it is (filterProfanity cannot take it).
 */
export const filteredText = (text) => {
  if (typeof text !== 'string' || !text) return text
  const kept = filteredTexts.has(text)
  const result = kept ? filteredTexts.get(text) : filterProfanity(text)
  // To the end, as shown last: the texts dropped are the ones shown least recently - never the
  // one just shown, however long.
  if (kept) filteredTexts.delete(text)
  else filteredChars += text.length
  filteredTexts.set(text, result)
  while (filteredChars > FILTERED_TEXT_CHARS && filteredTexts.size > 1) {
    const oldest = filteredTexts.keys().next().value
    filteredTexts.delete(oldest)
    filteredChars -= oldest.length
  }
  return result
}

/**
 * The class status row. `stats` is computeTelemetryStats on the merged telemetry: its
 * studentSummaries are the students with activity. Tests passed (avg) counts only those who
 * ran the tests (ranTests), and the AI figures only students who had AI chat, so a condition
 * without it does not pull them down.
 */
export const classStatus = ({ stats, students, aiUserIds, connectedNow }) => {
  const active = Object.entries(stats?.studentSummaries || {})
  const tested = active.filter(([, summary]) => summary.testsTotal > 0)
  const withAi = active.filter(([id]) => aiUserIds.has(String(id)))
  const questions = withAi.reduce((sum, [, summary]) => sum + (summary.chatCount || 0), 0)
  return {
    joined: students.length,
    connectedNow,
    withActivity: active.length,
    passedAll: active.filter(([, summary]) => summary.allPassed).length,
    ranTests: tested.length,
    avgTestsPassed: tested.length
      ? tested.reduce((sum, [, summary]) => sum + summary.testsPassed / summary.testsTotal, 0) / tested.length
      : null,
    ai: {
      students: withAi.length,
      usedAi: withAi.filter(([, summary]) => summary.chatCount > 0).length,
      questions,
      perStudent: withAi.length ? questions / withAi.length : null,
    },
  }
}

// The parts of a student's Monitor analytics (computeStudentAnalytics) that the taxonomy reads,
// built the same way - their events from first to last, segmented by autoSegmentEvents with
// each segment's suggested behaviour - without the rest of it, the Monitor's heaviest
// computation. null for a student the Monitor does not list.
export const taxonomyAnalytics = (events) => {
  if (!listedByMonitor(events)) return null
  const replayStartTime = events[0].timestamp
  const replayDuration = events[events.length - 1].timestamp - replayStartTime
  const autoSegments = autoSegmentEvents(events, replayStartTime, replayDuration).map((segment) => ({
    ...segment,
    behavior: segment.suggestedBehavior || segment.behavior,
  }))
  return { filteredEvents: events, replayStartTime, replayDuration, autoSegments }
}

// The stretches the taxonomy calls asking without trying.
const ASK_WINDOWS = ['ask-quick', 'ask-extended']

// A student's AI-use patterns on the Overview, for the card's counts and the student list's
// flags alike: the Monitor's behaviour-mode taxonomy (getStudentCategories), except that an
// 'ask' window counts only when it ends at a question - the stretch before one, from the start
// or from the previous question, with no code run or written. The stretch after the last
// question ends with no question, and it is all a student who never asked has.
export const aiUseCategories = (analytics) => {
  const sessionCategories = getStudentCategories(analytics, 'behavior').filter((key) => key in SESSION_TAXONOMY)
  // Window i runs up to question i + 1, so the first `asked` windows end at a question. By
  // index: a window that would be empty is skipped (when the newest event is a question).
  const asked = (analytics?.filteredEvents || []).filter((event) => event.type === 'CHAT_QUERY').length
  const windows = getStudentQueryWindows(analytics).filter(
    (window) => !ASK_WINDOWS.includes(window.category) || window.windowIndex < asked
  )
  const windowCategories = Object.keys(WINDOW_TAXONOMY).filter((key) => windows.some((window) => window.category === key))
  return [...sessionCategories, ...windowCategories]
}

// What the Overview keeps of a student's events: their AI-use patterns (aiUseCategories), and
// whether they asked the AI without trying - an 'ask' window, or several questions in a row
// with no work between ('repeated-no-effort'). null for a student the Monitor does not list.
export const studentPatterns = (events) => {
  const analytics = taxonomyAnalytics(events)
  if (!analytics) return null
  const categories = aiUseCategories(analytics)
  return {
    categories,
    askedWithoutTrying: categories.some((key) => ASK_WINDOWS.includes(key) || key === 'repeated-no-effort'),
  }
}

/**
 * How many students show each of the Monitor's AI-use patterns (studentTaxonomy, behaviour
 * mode - the counts on the Monitor's Sequences pills - as aiUseCategories reads them).
 * `patternsById`: studentPatterns per student. Only students who had AI chat are counted, so
 * 'No AI Used' never includes a student who could not use it.
 */
export const aiUsePatterns = (patternsById, aiUserIds) => {
  const ids = Object.keys(patternsById || {}).filter((id) => aiUserIds.has(String(id)))
  const counts = Object.fromEntries(Object.keys(TAXONOMY).map((key) => [key, 0]))
  for (const id of ids) {
    for (const key of patternsById[id]?.categories || []) {
      if (key in counts) counts[key] += 1
    }
  }
  return {
    students: ids.length,
    patterns: Object.entries(TAXONOMY).map(([key, { label }]) => ({ key, label, count: counts[key] })),
  }
}

// An error key's length, at most.
export const ERROR_KEY_MAX = 200
// A Python traceback's first line, and its frames' ('  File "<exec>", line 4, in total_of'),
// each with its source line and carets under it, indented further.
const TRACEBACK = 'Traceback (most recent call last):'
const FRAME_LINE = /^\s+File ".*", line \d+/
// A message's closing "Type: detail" line, as a Python error's ("NameError: name 'x' is not
// defined").
const TYPE_DETAIL_LINE = /^[A-Za-z_][\w.]*: /
// Where in the run's source Python found a syntax error, inside the message. The source a run
// executes has lines of its own before the student's code (TerminalContext), so these numbers
// are not the student's line numbers, and the same mistake would split into a key per line.
const SYNTAX_ERROR = /^(SyntaxError|IndentationError|TabError): /
const SOURCE_LINE = / on line \d+| \(detected at line \d+\)/g

/**
 * What a student's error (TERMINAL_ERROR's payload: { message, type }) is grouped by on the
 * Overview, with whitespace collapsed and cut to ERROR_KEY_MAX characters:
 *   - a Python traceback: the error's own line, e.g. "AttributeError: 'TaskList' object has no
 *     attribute 'tasks'" - the first line at the margin after the last frame and that frame's
 *     indented source and carets, so the last error when one led to another. Not always the
 *     message's last line: the error's message can run on ("ValueError: bad row\nrow 3 has 2
 *     columns"), and notes follow it. A syntax error's position in the run's source ("... after
 *     'for' statement on line 4") is left out.
 *   - another message that ends with a "Type: detail" line: that line.
 *   - otherwise (the runner's own, like "Python engine failed to load.", or a traceback with no
 *     such line): the event's type and the message's first line.
 */
export const errorKey = (payload) => {
  const raw = String(payload?.message ?? '').split('\n')
  const tidy = (line) => line.replace(/\s+/g, ' ').trim()
  const lines = raw.map(tidy).filter(Boolean)
  const last = lines[lines.length - 1] || ''
  let closing = null
  if (lines[0] === TRACEBACK) {
    // Read before tidying: what is under a frame is told apart by its indentation.
    let from = raw.findIndex((line) => tidy(line) === TRACEBACK) + 1
    raw.forEach((line, index) => {
      if (FRAME_LINE.test(line)) from = index + 1
    })
    const errorLine = raw.slice(from).find((line) => /^\S/.test(line))
    if (errorLine) closing = tidy(errorLine)
  } else if (TYPE_DETAIL_LINE.test(last)) {
    closing = last
  }
  const key = !closing
    ? [payload?.type || 'Error', lines[0]].filter(Boolean).join(': ')
    : SYNTAX_ERROR.test(closing)
      ? closing.replace(SOURCE_LINE, '')
      : closing
  return key.length > ERROR_KEY_MAX ? `${key.slice(0, ERROR_KEY_MAX - 1)}…` : key
}

// How far a student's events have to go on past a run that logged nothing - no error, output or
// test result, with the tests off - for it to count as ended with no error: past the workspace's
// 5-s telemetry batches, a Judge0 run's wait (its polling gives up after 15 s) and most first
// loads of the Python engine, during which the student can go on typing.
export const SILENT_RUN_MS = 20 * 1000

// From events[from] on, in one condition session's events: a run that ended with no error, or a
// test result with more than `passedThen` tests passing. A run has ended with no error once its
// end is recorded without one: its test result (a run logs its error first), its output with no
// error after it, or the next run (a workspace runs one at a time) - or, with the tests off
// (`testsOn` false), once the student's events go on SILENT_RUN_MS past a newest run that logged
// nothing. Until then its error may still be on its way: the Python engine may be loading, a
// Judge0 run waiting, and the workspace sends telemetry every 5 s. A reload (SESSION_START)
// before then ends the run with nothing known: what the page had not sent yet was lost with it.
const progressFrom = (events, from, passedThen, testsOn) => {
  // The run being read, { at, failed, output }; null before the first, and after a reload.
  let run = null
  for (let i = from; i < events.length; i += 1) {
    const { type, payload, timestamp } = events[i]
    if (type === 'TEST_CASE_RESULT') {
      if ((payload?.passed_count || 0) > passedThen || (run && !run.failed)) return true
    } else if (type === 'TERMINAL_RUN') {
      if (run && !run.failed) return true
      run = { at: timestamp, failed: false, output: false }
    } else if (type === 'SESSION_START') {
      if (run && !run.failed && run.output) return true
      run = null
    } else if (run && type === 'TERMINAL_ERROR') {
      run.failed = true
    } else if (run && type === 'TERMINAL_OUTPUT') {
      run.output = true
    }
  }
  if (!run || run.failed) return false
  return run.output || (!testsOn && events[events.length - 1].timestamp - run.at >= SILENT_RUN_MS)
}

/**
 * Whether a student has moved past their error at sessions[sessionId][index] - `sessions`
 * being their events per condition session (mergeConditions' sessionsByUser): a later run that
 * ended with no error, or a later test result with more tests passing than they had at the
 * error. Later is, in that session, from their next run on (the failing run's own test result
 * comes after its error), and in another session they work in, from their first run after the
 * error. `testsOn`: whether the exercise's test cases are on (progressFrom).
 */
export const movedPast = (sessions, sessionId, index, testsOn = true) => {
  const events = sessions[sessionId]
  let next = index + 1
  while (next < events.length && events[next].type !== 'TERMINAL_RUN') next += 1
  // Tests passing at the error: the latest result before the next run (the failing run's own).
  let passedThen = 0
  for (let i = next - 1; i >= 0; i -= 1) {
    if (events[i].type === 'TEST_CASE_RESULT') {
      passedThen = events[i].payload?.passed_count || 0
      break
    }
  }
  if (progressFrom(events, next, passedThen, testsOn)) return true
  const errorAt = events[index].timestamp
  return Object.entries(sessions).some(([id, other]) => {
    if (String(id) === String(sessionId)) return false
    const from = other.findIndex((event) => event.type === 'TERMINAL_RUN' && event.timestamp > errorAt)
    return from >= 0 && progressFrom(other, from, passedThen, testsOn)
  })
}

/**
 * A student's errors, grouped by errorKey: theirs on the Common errors card (commonErrors), and
 * in their detail - there for a student who has just started too, whom the card leaves out.
 * `sessions`: their events per condition session (mergeConditions' sessionsByUser); `testsOn`,
 * whether the exercise's test cases are on (movedPast).
 * Each: { key, count, lastAt, movedPast, last }, `last` being their latest such error as
 * { event, sessionId, index, run } - `index` its place in that condition session's events,
 * `run` the TERMINAL_RUN that raised it there (each session is its own workspace): the latest
 * one since the page was loaded, null when that run's own record is missing.
 */
export const studentErrors = (sessions, testsOn = true) => {
  const byKey = new Map()
  for (const [sessionId, sessionEvents] of Object.entries(sessions || {})) {
    let run = null
    // Whether `run` has already ended: a run logs at most one TERMINAL_ERROR, before its
    // TEST_CASE_RESULT, so an error after that came from a run whose TERMINAL_RUN was lost
    // (a telemetry batch that failed to save is not retried) - its code is not known.
    let ended = false
    sessionEvents.forEach((event, index) => {
      // A reload starts the workspace afresh: a run from before it raises nothing after it.
      if (event.type === 'SESSION_START') run = null
      else if (event.type === 'TERMINAL_RUN') {
        run = event
        ended = false
      } else if (event.type === 'TEST_CASE_RESULT') ended = true
      else if (event.type === 'TERMINAL_ERROR') {
        const key = errorKey(event.payload)
        const entry = byKey.get(key) || { count: 0, last: null }
        entry.count += 1
        if (!entry.last || event.timestamp >= entry.last.event.timestamp) {
          entry.last = { event, sessionId: Number(sessionId), index, run: ended ? null : run }
        }
        byKey.set(key, entry)
        ended = true
      }
    })
  }
  return [...byKey].map(([key, { count, last }]) => ({
    key,
    count,
    lastAt: last.event.timestamp,
    last,
    movedPast: movedPast(sessions, last.sessionId, last.index, testsOn),
  }))
}

/**
 * The class's common errors: every error (studentErrors) of the students the other figures
 * count (15 s of activity, as computeTelemetryStats), grouped by errorKey - the most students
 * first, then the most recent. `eventsByUser` and `sessionsByUser` are mergeConditions';
 * `testsOn`, whether the exercise's test cases are on (movedPast).
 * Each group: { key, students, occurrences, lastAt }. Its students, those who have not moved
 * past it first, then the most recent: their studentErrors entry for it, with their `id` -
 * { id, count, lastAt, movedPast, last }.
 */
export const commonErrors = ({ eventsByUser, sessionsByUser, testsOn = true }) => {
  const groups = new Map()
  for (const [id, events] of Object.entries(eventsByUser)) {
    if (!listedByMonitor(events)) continue
    for (const { key, ...entry } of studentErrors(sessionsByUser[id], testsOn)) {
      if (!groups.has(key)) groups.set(key, { key, students: [], occurrences: 0, lastAt: 0 })
      const group = groups.get(key)
      group.students.push({ id, ...entry })
      group.occurrences += entry.count
      group.lastAt = Math.max(group.lastAt, entry.lastAt)
    }
  }
  const list = [...groups.values()]
  list.forEach((group) => group.students.sort((a, b) => a.movedPast - b.movedPast || b.lastAt - a.lastAt))
  return list.sort((a, b) => b.students.length - a.students.length || b.lastAt - a.lastAt || a.key.localeCompare(b.key))
}

// A run's code as the files it holds, [{ name, code }]: a web exercise's run records its HTML,
// CSS and JS together as JSON; any other run's code is one file, without a name.
export const runFiles = (code) => {
  try {
    const parsed = JSON.parse(code)
    const files = ['html', 'css', 'js']
      .filter((name) => typeof parsed?.[name] === 'string' && parsed[name].trim())
      .map((name) => ({ name: name.toUpperCase(), code: parsed[name] }))
    if (files.length) return files
  } catch (error) {
    // Not JSON: one file.
  }
  return [{ name: null, code }]
}

/**
 * A student's latest event of a type across their condition sessions, by time - `sessions`
 * being their events per session (mergeConditions' sessionsByUser): { event, sessionId, index },
 * `index` its place in that session's events; null when they have none.
 */
export const latestEvent = (sessions, type) => {
  let latest = null
  for (const [sessionId, events] of Object.entries(sessions || {})) {
    for (let index = events.length - 1; index >= 0; index -= 1) {
      if (events[index].type !== type) continue
      if (!latest || events[index].timestamp > latest.event.timestamp) latest = { event: events[index], sessionId: Number(sessionId), index }
      break
    }
  }
  return latest
}

// The Code Coach chat's two roles, by CC_MESSAGE_RENDERED's role.
const CC_ROLE_NAMES = { coach: 'Code Coach', checker: 'Evidence Checker' }

/**
 * A student's conversation with the AI, oldest first, from their events in every condition
 * session (`sessions`: mergeConditions' sessionsByUser[id]) - the student dialog's Conversation
 * tab: [{ key, at, sessionId, who, name, text }].
 *   who: 'student' (a question, or an answer to the Code Coach's card - name 'Answer to the
 *     card'), 'ai' (name: 'AI', 'Code Coach' or 'Evidence Checker'), 'card' (the CC-M card's
 *     question) or 'notice' (a request that got no reply, a card skipped or cancelled);
 *   text: as written - the dialog shows it through the word filter.
 * The AI chat and the study tutor log CHAT_QUERY / CHAT_RESPONSE (a CHAT_QUERY without text is
 * a classification of the question, not one) and CHAT_ERROR for a request that failed. The Code
 * Coach chat logs CC_* events; its messages and card are logged again after a reload (restored,
 * the same messageId), so each is kept once, where it first appeared.
 */
export const studentConversation = (sessions) => {
  const items = []
  const seen = new Set()
  for (const [sessionId, events] of Object.entries(sessions || {})) {
    // "Start a new question" is logged before its call; a call that failed left the card open.
    const cancelFailed = new Set(
      (events || []).filter((event) => event.type === 'CC_FAILED' && event.payload?.stage === 'cancel').map((event) => event.payload?.roundId)
    )
    for (const [index, event] of (events || []).entries()) {
      const payload = event.payload || {}
      const base = { key: `${sessionId}:${event.event_id ?? index}`, at: event.timestamp, sessionId: Number(sessionId) }
      const add = (item) => items.push({ ...base, name: null, ...item })
      switch (event.type) {
        case 'CHAT_QUERY':
          if (typeof payload.text === 'string' && payload.text) add({ who: 'student', text: payload.text })
          break
        case 'CHAT_RESPONSE':
          if (typeof payload.content === 'string') add({ who: 'ai', name: 'AI', text: payload.content })
          break
        case 'CHAT_ERROR':
          add({ who: 'notice', text: 'The AI could not answer this request.' })
          break
        case 'CC_REQUEST_SENT':
          if (typeof payload.text === 'string' && payload.text) add({ who: 'student', text: payload.text })
          break
        case 'CC_MESSAGE_RENDERED':
        case 'CC_CARD_SHOWN': {
          const id = payload.messageId ?? `${payload.roundId}:${event.type}:${payload.role ?? ''}`
          if (seen.has(`${sessionId}:${id}`)) break
          seen.add(`${sessionId}:${id}`)
          if (event.type === 'CC_CARD_SHOWN') add({ who: 'card', text: payload.prompt ?? '' })
          else add({ who: 'ai', name: CC_ROLE_NAMES[payload.role] || 'AI', text: payload.text ?? '' })
          break
        }
        case 'CC_CONTRIBUTION_SUBMITTED':
          if (typeof payload.text === 'string' && payload.text) add({ who: 'student', name: 'Answer to the card', text: payload.text })
          break
        case 'CC_SKIPPED':
          add({ who: 'notice', text: 'Continued without answering the card.' })
          break
        case 'CC_ROUND_CANCELLED':
          if (!cancelFailed.has(payload.roundId)) add({ who: 'notice', text: 'Started a new question; the card was cancelled.' })
          break
        case 'CC_FAILED':
          // A call that should have brought a reply; not the chat's own loading ('state') or a
          // cancel that did not go through.
          if (['request', 'contribution', 'checker'].includes(payload.stage)) add({ who: 'notice', text: 'The tutor could not answer this request.' })
          break
        default:
          break
      }
    }
  }
  return items.sort((a, b) => a.at - b.at)
}

/**
 * A student's events with the Code Coach chat's (CC_*) turned into the ones the replay's chat
 * plays (CHAT_QUERY / CHAT_RESPONSE) - the student dialog's Replay tab (StudentReplay): their
 * question and their answer to the card as theirs, each role's message and the card as the AI's,
 * prefixed with who wrote it - once each, as a reload logs them again (restored, the same
 * messageId). Typing in the card's own box (input: 'card') is left out: the replay would show
 * it in the chat box. Every other event is kept as it is.
 */
export const replayChatEvents = (events) => {
  const seen = new Set()
  const out = []
  for (const event of events || []) {
    const payload = event.payload || {}
    if (['CHAT_TYPE', 'CHAT_PASTE', 'CHAT_DELETE'].includes(event.type) && payload.input === 'card') continue
    if (event.type === 'CC_REQUEST_SENT' && payload.text) {
      out.push({ ...event, type: 'CHAT_QUERY', payload: { text: payload.text } })
    } else if (event.type === 'CC_CONTRIBUTION_SUBMITTED' && payload.text) {
      out.push({ ...event, type: 'CHAT_QUERY', payload: { text: `(Answer to the card) ${payload.text}` } })
    } else if (event.type === 'CC_MESSAGE_RENDERED' || event.type === 'CC_CARD_SHOWN') {
      const id = payload.messageId ?? `${payload.roundId}:${event.type}:${payload.role ?? ''}`
      if (seen.has(id)) continue
      seen.add(id)
      const content = event.type === 'CC_CARD_SHOWN' ? `Card: ${payload.prompt ?? ''}` : `${CC_ROLE_NAMES[payload.role] || 'AI'}: ${payload.text ?? ''}`
      out.push({ ...event, type: 'CHAT_RESPONSE', payload: { content } })
    } else {
      out.push(event)
    }
  }
  return out
}

/**
 * The run behind the test result at events[index], in one condition session's events: the
 * latest TERMINAL_RUN before it, or null when that run's own record is missing. A run logs at
 * most one TERMINAL_ERROR and then its TEST_CASE_RESULT, so an earlier result, a second error
 * or a reload (SESSION_START) in between means the run that got this result was not recorded
 * (a telemetry batch that failed to save is not retried), not that the run before it got it.
 */
export const runBehind = (events, index) => {
  let errors = 0
  for (let i = index - 1; i >= 0; i -= 1) {
    const { type } = events[i]
    if (type === 'TERMINAL_RUN') return events[i]
    if (type === 'TEST_CASE_RESULT' || type === 'SESSION_START') return null
    if (type === 'TERMINAL_ERROR' && (errors += 1) > 1) return null
  }
  return null
}

/**
 * What became of the run at events[index], in one condition session's events, for a
 * student's 'Latest code': { error } - the error it raised (TERMINAL_ERROR), with or without a
 * test result after it - else { result } - its test result - else { clean: true } once it
 * ended with neither (the tests off: its output, the next run, or the student's events going
 * on SILENT_RUN_MS past it, as for movedPast), else {} - still going, or its end not recorded
 * (a reload before it, a record that failed to save). `testsOn`: whether the exercise's test
 * cases are on; with them on, a run ends with its test result.
 */
export const runOutcome = (events, index, testsOn = true) => {
  let output = false
  for (let i = index + 1; i < events.length; i += 1) {
    const { type } = events[i]
    if (type === 'TERMINAL_ERROR') return { error: events[i] }
    if (type === 'TEST_CASE_RESULT') return { result: events[i] }
    if (type === 'TERMINAL_OUTPUT') output = true
    else if (type === 'TERMINAL_RUN') return !testsOn ? { clean: true } : {}
    else if (type === 'SESSION_START') return !testsOn && output ? { clean: true } : {}
  }
  const quiet = events[events.length - 1].timestamp - events[index].timestamp >= SILENT_RUN_MS
  return !testsOn && (output || quiet) ? { clean: true } : {}
}

// The name the workspace gives each test of a run's result (TerminalContext) - a web
// exercise's checks carry their own names there instead.
const PLACEHOLDER_TEST_NAME = /^Test Case \d+$/

/**
 * A test's name on the Overview, from its position in a result's detailed_results: the
 * testName of the test_list row at that position - the list of the condition session the
 * result came from, which the run's harness was built from - else the name the result itself
 * gives (`reported`) unless it is the workspace's placeholder, else 'Test N'.
 */
export const testNameAt = (testList, position, reported) => {
  const own = (Array.isArray(testList) ? testList : [])[position]?.testName
  const given = typeof reported === 'string' && !PLACEHOLDER_TEST_NAME.test(reported.trim()) ? reported.trim() : ''
  return (typeof own === 'string' && own.trim()) || given || `Test ${position + 1}`
}

// A test result's tests (TEST_CASE_RESULT's payload), [{ name, passed }] in its order, named
// by testNameAt with `testList`, its condition session's. [] for a result without them.
export const resultTests = (payload, testList) =>
  (Array.isArray(payload?.detailed_results) ? payload.detailed_results : []).map((detail, position) => ({
    name: testNameAt(testList, position, detail?.test_name),
    passed: detail?.passed === true,
  }))

// The same tests by name, name -> passed: tests that share a name pass only together.
const testsByName = (tests) => {
  const byName = new Map()
  tests.forEach(({ name, passed }) => byName.set(name, byName.get(name) !== false && passed))
  return byName
}

/**
 * The tests students are stuck on: for each test, the students whose latest test result -
 * across their condition sessions, by time - fails it. It catches logic mistakes too, which
 * raise no error. Among the students the other figures count (15 s of activity, as
 * computeTelemetryStats) who have a test result. Tests are told apart by name (resultTests,
 * each result named by its own condition session's test_list): the conditions copy one
 * exercise's tests, but a condition edited since may order or name them differently.
 *   eventsByUser / sessionsByUser: mergeConditions'.
 *   testListBySession: session id -> that condition session's test_list.
 *   testsOn: whether the exercise's test cases are on; off, there is nothing to fail (null).
 * Returns null with the tests off, else { students, tests }: `students`, how many have a test
 * result; `tests`, those someone is failing, the most students first. Each test: { name, of,
 * failing, since } - `of` the students whose latest result has it, `since` the earliest of
 * theirs. Its failing students, the longest failing first: { id, since, latest } - `since`
 * when their unbroken stretch of results failing it began; `latest` their latest result, as
 * latestEvent's with `run`, the run behind it (runBehind).
 */
export const stuckTests = ({ eventsByUser, sessionsByUser, testListBySession = {}, testsOn = true }) => {
  if (!testsOn) return null
  let students = 0
  const tests = new Map()
  for (const [id, events] of Object.entries(eventsByUser)) {
    if (!listedByMonitor(events)) continue
    const sessions = sessionsByUser[id] || {}
    // Their test results across sessions, in time order, each with its tests by name.
    const results = []
    for (const [sessionId, sessionEvents] of Object.entries(sessions)) {
      sessionEvents.forEach((event, index) => {
        if (event.type === 'TEST_CASE_RESULT') results.push({ event, sessionId: Number(sessionId), index })
      })
    }
    if (!results.length) continue
    students += 1
    results.sort((a, b) => a.event.timestamp - b.event.timestamp)
    const byName = results.map(({ event, sessionId }) => testsByName(resultTests(event.payload, testListBySession[sessionId])))
    const last = results.length - 1
    let latest = null
    for (const [name, passed] of byName[last]) {
      if (!tests.has(name)) tests.set(name, { name, of: 0, failing: [], since: Infinity })
      const test = tests.get(name)
      test.of += 1
      if (passed) continue
      let first = last
      while (first > 0 && byName[first - 1].get(name) === false) first -= 1
      if (!latest) {
        const { sessionId, index } = results[last]
        latest = { ...results[last], run: runBehind(sessions[sessionId], index) }
      }
      test.failing.push({ id, since: results[first].event.timestamp, latest })
      test.since = Math.min(test.since, results[first].event.timestamp)
    }
  }
  const failed = [...tests.values()].filter((test) => test.failing.length)
  failed.forEach((test) => test.failing.sort((a, b) => a.since - b.since || compareIds(a.id, b.id)))
  return {
    students,
    tests: failed.sort((a, b) => b.failing.length - a.failing.length || a.since - b.since || a.name.localeCompare(b.name)),
  }
}

/**
 * The student list, those most likely to need help first. Each row: { student, summary,
 * flags, homeKey, idleMs }. A student who passed every test has no flags and sorts last; a
 * flag's weight orders the rest, then fewer tests passed, then longer time on task.
 *   patternsById: studentPatterns per student.
 *   fetchedAtByUser: when each student's telemetry was fetched (mergeConditions). Idle time
 *     (idleMs, null without activity) runs up to then, not up to now: the data says nothing
 *     about later, and after the page was hidden, or while a condition's requests fail, it
 *     can be minutes old. The Inactive flag and the Last active column both read it.
 *   testsOn: whether the exercise has test cases. Without them nobody passes a test, so
 *     "no test passing" says nothing.
 *   numbers: studentNumbers', to order students alike by their number rather than their name.
 *   eventsByUser: mergeConditions', to tell a student who has just started - events, but not
 *     yet the 15 s of activity the figures count (no summary) - from one with none.
 */
export const studentsByNeed = ({ students, stats, patternsById, homeByUser, fetchedAtByUser, testsOn = true, numbers, eventsByUser }) => {
  const summaries = stats?.studentSummaries || {}
  const rows = students.map((student) => {
    const id = String(student.id)
    const summary = summaries[id] || null
    const fetchedAt = fetchedAtByUser?.[id]
    const idleMs = summary?.lastActive && fetchedAt ? fetchedAt - Date.parse(summary.lastActive) : null
    const flags = []
    if (!summary) {
      flags.push(
        eventsByUser?.[id]?.length
          ? { key: 'just-started', label: 'Just started', weight: 1 }
          : { key: 'no-activity', label: 'No activity yet', weight: 1 }
      )
    } else if (!summary.allPassed) {
      if (testsOn && summary.testsPassed === 0 && summary.duration >= STUCK_AFTER_SECONDS) {
        flags.push({ key: 'stuck', label: `No test passing after ${Math.round(summary.duration / 60)} min`, weight: 3 })
      }
      if (idleMs != null && idleMs >= INACTIVE_AFTER_MS) {
        flags.push({ key: 'inactive', label: `Inactive for ${Math.round(idleMs / 60000)} min`, weight: 2 })
      }
      const patterns = patternsById?.[id]
      const categories = patterns?.categories || []
      if (categories.includes('debug-loop')) flags.push({ key: 'debug-loop', label: 'Debug loop', weight: 2 })
      if (patterns?.askedWithoutTrying) flags.push({ key: 'ask-without-trying', label: 'Asks the AI without trying', weight: 2 })
      if (categories.includes('query-burst')) flags.push({ key: 'query-burst', label: 'Many AI questions in a row', weight: 1 })
    }
    return {
      student,
      summary,
      flags,
      homeKey: homeByUser[id] || null,
      idleMs,
      finished: Boolean(summary?.allPassed),
      weight: flags.reduce((sum, flag) => sum + flag.weight, 0),
      passedShare: summary?.testsTotal ? summary.testsPassed / summary.testsTotal : 0,
    }
  })
  const number = (row) => numbers?.[String(row.student.id)] ?? Infinity
  return rows.sort(
    (a, b) =>
      a.finished - b.finished ||
      b.weight - a.weight ||
      a.passedShare - b.passedShare ||
      (b.summary?.duration || 0) - (a.summary?.duration || 0) ||
      (numbers ? number(a) - number(b) : 0) ||
      displayName(a.student).localeCompare(displayName(b.student))
  )
}
